import { useMemo, useRef } from "react";

/**
 * Lightweight projection of a message used for rendering metadata
 * (date dividers, compact-grouping) — decoupled from message content
 * so an edit to a message body doesn't trigger the renderedRows memo
 * to rebuild. Only new/removed messages or `user_id`/`created_at`/
 * `kind` changes do.
 */
export type WindowedItem = {
  id: string;
  user_id: string;
  created_at: number;
  kind?: string | null;
  /** Inline-reply citation FK. Drives the `is-message-compact` carve-out
   *  that forces a fresh avatar header on every inline-reply row — Discord
   *  shape: a quote-cited reply must read as self-contained, never as a
   *  same-author continuation. */
  replied_to_message_id?: string | null;
};

/**
 * Source row shape — only the fields the projection reads. Lets this
 * hook accept either the full `messages.byChannelComposite` row or any
 * compatible projection without coupling to the schema type.
 */
export interface WindowedItemSource {
  id: string;
  user_id: string;
  created_at: number;
  kind?: string | null;
  replied_to_message_id?: string | null;
}

/**
 * Builds an identity-stable `WindowedItem[]` from `windowMessages` and
 * a matching `WindowedItem | null` from `predecessorMessage`. Returns
 * the same array reference across renders when the projected fields
 * are unchanged, so downstream `useMemo`s short-circuit.
 *
 * `predecessorItem` depends only on the predecessor's primitive fields
 * (not the message-row identity) so unrelated content updates upstream
 * don't churn it.
 */
export function useWindowedItems(
  windowMessages: readonly WindowedItemSource[],
  predecessorMessage: WindowedItemSource | null,
): { windowedItems: WindowedItem[]; predecessorItem: WindowedItem | null } {
  const prevWindowedItemsRef = useRef<WindowedItem[]>([]);
  const windowedItems = useMemo(() => {
    const next = windowMessages.map(
      (m): WindowedItem => ({
        id: m.id,
        user_id: m.user_id,
        created_at: m.created_at,
        kind: m.kind,
        replied_to_message_id: m.replied_to_message_id ?? null,
      }),
    );
    const prev = prevWindowedItemsRef.current;
    if (
      next.length === prev.length &&
      next.every(
        (n, i) =>
          n.id === prev[i].id &&
          n.user_id === prev[i].user_id &&
          n.created_at === prev[i].created_at &&
          n.kind === prev[i].kind &&
          n.replied_to_message_id === prev[i].replied_to_message_id,
      )
    ) {
      return prev;
    }
    prevWindowedItemsRef.current = next;
    return next;
  }, [windowMessages]);

  const predecessorItem: WindowedItem | null = useMemo(() => {
    if (!predecessorMessage) return null;
    return {
      id: predecessorMessage.id,
      user_id: predecessorMessage.user_id,
      created_at: predecessorMessage.created_at,
      kind: predecessorMessage.kind,
      replied_to_message_id: predecessorMessage.replied_to_message_id ?? null,
    };
    // Dependencies on primitive fields (not the message object identity)
    // keep this stable across unrelated message updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    predecessorMessage?.id,
    predecessorMessage?.user_id,
    predecessorMessage?.created_at,
    predecessorMessage?.kind,
    predecessorMessage?.replied_to_message_id,
  ]);

  return { windowedItems, predecessorItem };
}
